import { useEffect, useState } from "react";
import api, { errMsg, fmtMinutes } from "@/lib/api";
import { toast } from "sonner";

export default function Billing() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/billing/summary").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!data) return <p className="text-muted-foreground font-mono-digits text-sm animate-pulse">Loading…</p>;

  const cards = [
    { label: "Total billable hours", value: data.total_hours, testid: "billing-total-hours" },
    { label: "Total payout due", value: `R${data.total_amount.toLocaleString()}`, testid: "billing-total-amount" },
    { label: "Time entries", value: data.total_entries, testid: "billing-total-entries" },
  ];

  return (
    <div className="space-y-8" data-testid="billing-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Billing & Payouts</h1>
        <p className="text-sm text-muted-foreground mt-1">Hours × hourly rate, per agent and per ticket. Invoice-ready.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-border bg-card p-5 hover:border-orange-500/40 transition-colors">
            <p className="font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground">{c.label}</p>
            <p data-testid={c.testid} className="font-mono-digits text-2xl sm:text-3xl font-bold mt-2 tabular-nums">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold">Per agent</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-6 py-3">Agent</th>
                <th className="px-6 py-3">Rate</th>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3">Hours</th>
                <th className="px-6 py-3">Payout due</th>
              </tr>
            </thead>
            <tbody>
              {data.per_agent.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-muted-foreground text-sm">No billable time yet.</td></tr>
              )}
              {data.per_agent.map((a) => (
                <tr key={a.user_id} data-testid={`billing-agent-${a.user_id}`} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                  <td className="px-6 py-3 font-semibold">{a.name}</td>
                  <td className="px-6 py-3 font-mono-digits text-muted-foreground">R{a.hourly_rate}/h</td>
                  <td className="px-6 py-3 font-mono-digits">{fmtMinutes(a.minutes)}</td>
                  <td className="px-6 py-3 font-mono-digits">{a.hours}</td>
                  <td className="px-6 py-3 font-mono-digits text-emerald-400 font-bold">R{a.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold">Per ticket</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-digits text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-6 py-3">Ticket</th>
                <th className="px-6 py-3">Subject</th>
                <th className="px-6 py-3">Time</th>
                <th className="px-6 py-3">Cost</th>
              </tr>
            </thead>
            <tbody>
              {data.per_ticket.length === 0 && (
                <tr><td colSpan={4} className="px-6 py-8 text-center text-muted-foreground text-sm">No billable time yet.</td></tr>
              )}
              {data.per_ticket.map((t) => (
                <tr key={t.ticket_id} data-testid={`billing-ticket-${t.ticket_id}`} className="border-b border-border/50 hover:bg-secondary/30 transition-colors">
                  <td className="px-6 py-3 font-mono-digits text-orange-400">{t.ticket_number}</td>
                  <td className="px-6 py-3">{t.subject}</td>
                  <td className="px-6 py-3 font-mono-digits">{fmtMinutes(t.minutes)}</td>
                  <td className="px-6 py-3 font-mono-digits text-emerald-400">R{t.amount.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
